import React, { useEffect } from 'react';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { Form } from 'react-bootstrap';
import {
  assignmentAttributeToItem,
  deleteAssignmentAttribute,
  getItemAttributByItemId
} from '@store/INV/Attributes/AttributesSlice';
import NtxTable from '@components/DataTable/NtxTable';
import confirmAction from '@components/Shared/Alert/confirmAction';
import useGetAttributeList from '@hooks/INV/Attributes/useGetAttributeList';
import useGetUnitsOfMeasureList from '@hooks/INV/useGetUnitsOfMeasureList';
import { AttributeToItemSchema } from '../../../../ValidationForm/validationSchema';
import { usePermissions } from '@Helper/Permisstions/usePermissions';

const Attributes = () => {
  const { HasPermission } = usePermissions();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { ItemId } = useParams();
  const { UnitsOfMeasureList, loading: loading1, error: error1 } = useGetUnitsOfMeasureList();
  const { MasterItemsById: ItemById } = useAppSelector(a => a.MasterItemsINV);
  const { ItemConversionListByItem: uomListByItem } = useAppSelector(a => a.ItemConversion);
  const { AttributesList } = useGetAttributeList();
  const { AttributesByItem, error, loading } = useAppSelector(s => s.Attributes);

  useEffect(() => {
    if (ItemId) {
      dispatch(getItemAttributByItemId(ItemId));
    }
  }, [ItemId, dispatch]);

  const uomList = UnitsOfMeasureList?.filter(
    f => uomListByItem?.some(s => s.uom === f.id) || f.id === ItemById.unitOfMeasure
  );

  const formik = useFormik({
    initialValues: {
      itemId: ItemId,
      uom: '',
      attributeId: ''
    },
    validationSchema: AttributeToItemSchema(t),
    onSubmit: values => {
      dispatch(
        assignmentAttributeToItem({
          itemId: values.itemId,
          uom: values.uom,
          attributeId: values.attributeId
        })
      )
        .unwrap()
        .then(res => {
          if (res.succeeded === true) {
            CustomAlert({ action: 'Add' });
            dispatch(getItemAttributByItemId(ItemId || ''));
          } else {
            CustomAlert({ action: 'Error', msg: res.message });
          }
        })
        .catch(error => {
          CustomAlert({ action: 'Error' });
        })
        .finally(() => formik.setSubmitting(false));
    }
  });

  const handleDelete = async (item: any) => {
    const isConfirm = await confirmAction(t('ConfirmDelete'));
    if (!isConfirm) {
      return;
    }

    const body = {
      itemId: item.itemId,
      uom: item.uom,
      attributeId: item.attributeId
    };

    dispatch(deleteAssignmentAttribute(body))
      .unwrap()
      .then(() => {
        dispatch(getItemAttributByItemId(ItemId || ''));
        CustomAlert({ action: 'Delete' });
      })
      .catch(error => {
        CustomAlert({ action: 'Error' });
      });
  };

  const columns = [
    { title: t('Attribute'), key: 'Name' },
    { title: t('Unit'), key: 'uom' },
    { title: t(''), key: 'action', className: 'text-end', sortable: false }
  ];

  const tableData = AttributesByItem?.map(item => {
    return {
      Name: i18n.language === 'en' ? item.attributeName : item.attributeName2,

      uom:
        i18n.language === 'en'
          ? uomList.find(f => f.id === item.uom)?.name
          : uomList.find(f => f.id === item.uom)?.name2,

      action: (
        <button
          hidden={!HasPermission('INVUpdateItemFeatures')}
          type='button'
          className='btn  btn-outline-danger'
          onClick={() => handleDelete(item)}
        >
          <i className='fas fa-lg fa-trash-alt'></i>
        </button>
      )
    };
  });

  return (
    <>
      <Form onSubmit={formik.handleSubmit}>
        <div className='row align-items-end'>
          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'> {t('Item')}</label>
              <label className='form-control text-theme'>
                {i18n.language === 'en' ? ItemById?.name : ItemById?.name2}
              </label>
            </div>
          </div>

          <div className='col-xl-2'>
            <div className='form-group mb-3'>
              <label className='form-label'> {t('Unit')}</label>
              <Form.Select
                className='form-select'
                name='uom'
                onChange={formik.handleChange}
                value={formik.values.uom || ''}
                isInvalid={!!(formik.touched.uom && formik.errors.uom)}
              >
                <option value=''>{t('Choose')}</option>
                {uomList &&
                  uomList?.map((item, idx) => (
                    <option key={++idx} value={item.id}>
                      {i18n.language === 'en' ? item.name : item.name2}
                    </option>
                  ))}
              </Form.Select>
            </div>
          </div>

          <div className='col-xl-4'>
            <div className='form-group mb-3'>
              <label className='form-label'> {t('Attribute')}</label>
              <Form.Select
                className='form-select'
                name='attributeId'
                onChange={formik.handleChange}
                value={formik.values.attributeId || ''}
                isInvalid={!!(formik.touched.attributeId && formik.errors.attributeId)}
              >
                <option value=''>{t('Choose')}</option>
                {AttributesList &&
                  AttributesList?.map((item, idx) => (
                    <option key={++idx} value={item.id}>
                      {i18n.language === 'en' ? item.name : item.name2}
                    </option>
                  ))}
              </Form.Select>
            </div>
          </div>

          <div className='col-xl-2 text-end  '>
            <div className=' form-group mb-3 d-flex justify-content-end '>
              <button
                hidden={!HasPermission('INVUpdateItemFeatures')}
                disabled={formik.isSubmitting}
                type='submit'
                className='btn me-1 btn-theme   form-control'
              >
                {formik.isSubmitting ? (
                  <div className='spinner-border spinner-border-sm me-2'></div>
                ) : (
                  <i className='fas fa-lg fa-fw me-2 fa-plus'></i>
                )}
                {t('Add')}
              </button>
            </div>
          </div>
        </div>
      </Form>

      <NtxTable
        data={tableData || []}
        columns={columns}
        dataPerPage={10}
        theadClass=' w'
        tableClass='table text-nowrap table-hover '
        messageEmpty={t('NoAttributesForItem')}
      />
    </>
  );
};

export default Attributes;
